import { useState, useMemo, useCallback } from 'react';
import { api } from '@/services/api-class';

export interface NPSDSearchFilter {
  field: string;
  operator: string;
  value: string;
}

export interface UseNPSDSearchOptions {
  entityApiName: string;  // 'Person', 'Business', 'Vehicle', 'client'
  searchMode?: string;
}

export function useNPSDSearch({ entityApiName, searchMode = 'default' }: UseNPSDSearchOptions) {
  const [results, setResults] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const showViewButton = useMemo(() => {
    return ['default', 'addviewConnection', 'addviewRpt', 'addLinkConnection'].indexOf(searchMode) >= 0;
  }, [searchMode]);

  const showAddButton = useMemo(() => {
    return ['addConnection', 'addviewConnection', 'addRpt', 'addviewRpt'].indexOf(searchMode) >= 0;
  }, [searchMode]);

  const showLinkButton = useMemo(() => {
    return searchMode === 'addLinkConnection' || searchMode === 'citationLink' || searchMode === 'citationVehicleLink';
  }, [searchMode]);

  const loadResults = useCallback((page: number, size: number, filters: NPSDSearchFilter[]) => {
    setIsLoading(true);

    const searchData = {
      data: {
        take: size,
        skip: (page - 1) * size,
        page: page,
        pageSize: size,
        filter: {
          logic: 'and',
          filters: filters,
        },
      },
    };

    api.npsdSearch(
      entityApiName,
      page,
      size.toString(),
      JSON.stringify(searchData),
      (result: any) => {
        setResults(result.entries || []);
        setTotalRecords(result.records || 0);
        setHasSearched(true);
        setIsLoading(false);
      }
    );
  }, [entityApiName]);

  const performSearch = useCallback((filters: NPSDSearchFilter[]) => {
    setCurrentPage(1);
    loadResults(1, pageSize, filters);
  }, [loadResults, pageSize]);

  const clearResults = useCallback(() => {
    setResults([]);
    setTotalRecords(0);
    setCurrentPage(1);
    setHasSearched(false);
  }, []);

  const handlePageChange = useCallback((page: number, filters: NPSDSearchFilter[]) => {
    setCurrentPage(page);
    loadResults(page, pageSize, filters);
  }, [loadResults, pageSize]);

  const handlePageSizeChange = useCallback((size: number, filters: NPSDSearchFilter[]) => {
    setPageSize(size);
    setCurrentPage(1);
    loadResults(1, size, filters);
  }, [loadResults]);

  return {
    results,
    totalRecords,
    currentPage,
    pageSize,
    isLoading,
    hasSearched,
    showViewButton,
    showAddButton,
    showLinkButton,
    loadResults,
    performSearch,
    clearResults,
    handlePageChange,
    handlePageSizeChange,
  };
}
